import {
	IListAttrs,
	IListColumnConfig,
	IListOperations,
} from '@datachain/ui-sdk/common';
import { immerable, produce } from 'immer';
import { Map, Set } from 'immutable';

import { DatablockItemEntity } from './datablock-item.entity';

export interface IDatablockListExtra
	extends IListColumnConfig<DatablockItemEntity> {
	isFixed?: boolean;
	isSelectable?: boolean;
	isSearchable?: boolean;
	isInGroup?: boolean;
	isVisible?: boolean;
	canBeHidden?: boolean;
	canBeReordered?: boolean;
	canBeChosen?: boolean;
	format?: string;
	visibleIndex?: number;
	visible?: boolean;
	hasHeaderFilter?: boolean;
}

interface IDatablockListEntity extends IListAttrs<DatablockItemEntity> {
	availableColumns: Map<string, IDatablockListExtra>;
}

const entityDefaults: IDatablockListEntity = {
	elements: Map(),
	availableColumns: Map(),
};

export class DatablockListEntity
	implements
		IDatablockListEntity,
		IListOperations<DatablockListEntity, DatablockItemEntity>
{
	private [immerable] = true;
	public elements = entityDefaults.elements;
	public availableColumns = entityDefaults.availableColumns;

	public static build(
		availableCols: Array<IDatablockListExtra>
	): DatablockListEntity {
		const inst = new DatablockListEntity();
		const cols = availableCols.reduce((acc, curr) => {
			acc.push([curr.field, curr]);
			return acc;
		}, new Array<[string, IDatablockListExtra]>());
		inst.availableColumns = Map(cols);
		return inst;
	}

	private constructor() {}

	public getAvailableColumns(): Map<
		string,
		IListColumnConfig<DatablockItemEntity>
	> {
		return this.availableColumns;
	}

	public setElements(
		elems: Map<string, DatablockItemEntity>
	): DatablockListEntity {
		return produce(this, (draft: DatablockListEntity) => {
			draft.elements = elems;
		});
	}

	public setActionInProgress(
		id: number,
		value: boolean,
		jobId: string
	): DatablockListEntity {
		return produce(this, (draft: DatablockListEntity) => {
			const maybeFound = draft.elements.get(`${id}`);
			if (!maybeFound) {
				return;
			}
			draft.elements = draft.elements.set(
				`${id}`,
				maybeFound.setActionInProgress(value).setJobId(jobId)
			);
		});
	}

	public updatePersistenceInfo(
		id: number,
		info: {
			isPersisted: boolean;
			partitionCount: number;
			persistenceDate: Date | null;
			persistenceDuration: number;
			persistenceSize: number;
		}
	): DatablockListEntity {
		return produce(this, (draft: DatablockListEntity) => {
			const maybeFound = draft.elements.get(`${id}`);
			if (!maybeFound) {
				return;
			}
			draft.elements = draft.elements.set(
				`${id}`,
				maybeFound.setPersistenceInfo(info)
			);
		});
	}

	public setJobId(id: number, value: string | null): DatablockListEntity {
		return produce(this, (draft: DatablockListEntity) => {
			const maybeFound = draft.elements.get(`${id}`);
			if (!maybeFound) {
				return;
			}
			draft.elements = draft.elements.set(`${id}`, maybeFound.setJobId(value));
		});
	}

	public extractUniqTags(): Set<string> {
		return this.elements.reduce((acc, datablockItem) => {
			if (!datablockItem.tags) {
				return acc;
			}
			return Set([
				...acc.toArray(),
				...datablockItem.tags
					.toList()
					.toArray()
					.map((l) => l.label),
			]);
		}, Set<string>());
	}

	public extractModifiers(): Set<string> {
		return this.elements.reduce((acc, datablockItem) => {
			if (
				datablockItem.updatedByAvatar &&
				datablockItem.updatedByAvatar?.name
			) {
				acc = acc.add(datablockItem.updatedByAvatar.name);
			}
			return acc;
		}, Set<string>());
	}

	public extractActionCreators(): Set<string> {
		return this.elements.reduce((acc, datablockItem) => {
			if (datablockItem.createdByAvatar && datablockItem.createdByAvatar.name) {
				acc = acc.add(datablockItem.createdByAvatar.name);
			}
			return acc;
		}, Set<string>());
	}
}
